/**
 * Dots for the horizontal service carousel
 * (templates/components/common/services_showcase_v3.html).
 *
 * The track is a CSS scroll-snap row, so swiping already works with no JS —
 * this only adds the position indicator and lets a dot scroll to its card. If
 * the script never runs, the dots stay hidden and nothing else changes.
 *
 * A section opts in with `data-service-carousel`; inside it the scroller carries
 * `.js-carousel-track` and the (empty) dot row carries `.js-carousel-dots`.
 */
class ServiceCarousel {
    constructor(section) {
        this.track = section.querySelector('.js-carousel-track');
        this.dotsHost = section.querySelector('.js-carousel-dots');
        this.cards = this.track ? Array.from(this.track.children) : [];
        this.dots = [];
        this.ticking = false;
        this.onScroll = this.onScroll.bind(this);
    }

    init() {
        if (!this.track || !this.dotsHost || this.cards.length < 2) return;

        this.cards.forEach((card, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className =
                'h-1.5 rounded-full bg-[#D4D4D8] transition-all duration-200 ' +
                '[&.is-active]:w-6 [&.is-active]:bg-[#161616] w-1.5';
            dot.setAttribute('aria-label', `Go to service ${index + 1}`);
            dot.addEventListener('click', () => {
                this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
            });
            this.dotsHost.appendChild(dot);
            this.dots.push(dot);
        });

        // `flex` is added here rather than in the markup so the row cannot show
        // up as a stray block if the stylesheet's display order ever changes.
        this.dotsHost.classList.remove('hidden');
        this.dotsHost.classList.add('flex');
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.update();
    }

    onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
        });
    }

    update() {
        // Nearest card to the scroller's left edge wins — with scroll-snap that
        // is the card the reader is actually looking at.
        const left = this.track.scrollLeft + this.track.offsetLeft;
        let nearest = 0;
        let best = Infinity;

        this.cards.forEach((card, index) => {
            const distance = Math.abs(card.offsetLeft - left);
            if (distance < best) {
                best = distance;
                nearest = index;
            }
        });

        this.dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === nearest);
            dot.setAttribute('aria-current', index === nearest ? 'true' : 'false');
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document
        .querySelectorAll('[data-service-carousel]')
        .forEach((section) => new ServiceCarousel(section).init());
});
